/**
 * AuthActionRouter
 *
 * Routes Firebase action links (resetPassword, verifyEmail, etc.) to the
 * correct page, with meta-refresh and manual fallbacks for reliability.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import useDarkMode from '../../../hooks/useDarkMode';
import { SimpleLandingHeader } from '@/components/landing/shared/SimpleLandingHeader/SimpleLandingHeader';
import AuthLoadingScreen from '../../../components/shared/AuthLoadingScreen';

export default function AuthActionRouter() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState('');
  const isDark = useDarkMode();

  // Add meta refresh as fallback
  useEffect(() => {
    const mode = searchParams.get('mode');
    const queryString = searchParams.toString();
    
    if (mode && queryString) {
      let redirectUrl = '/signin'; // default fallback
      
      switch (mode) {
        case 'resetPassword':
          redirectUrl = `/update-password?${queryString}`;
          break;
        case 'verifyEmail':
        case 'verifyAndChangeEmail':
        case 'recoverEmail':
          redirectUrl = `/verify-email?${queryString}`;
          break;
        case 'signIn':
          redirectUrl = `/signin?${queryString}`;
          break;
      }
      
      // Add meta refresh tag as fallback (shorter interval)
      const metaRefresh = document.createElement('meta');
      metaRefresh.httpEquiv = 'refresh';
      metaRefresh.content = `2; url=${redirectUrl}`;
      document.head.appendChild(metaRefresh);
      
      console.log('Auth Action Router: Added meta refresh fallback to:', redirectUrl);
      
      return () => {
        if (document.head.contains(metaRefresh)) {
          document.head.removeChild(metaRefresh);
        }
      };
    }
  }, [searchParams]);

  useEffect(() => {
    let timeoutId;
    
    const processAction = async () => {
      try {
        console.log('Auth Action Router: Starting to process action');
        console.log('Auth Action Router: Current URL:', window.location.href);
        console.log('Auth Action Router: Search params:', searchParams.toString());

        const mode = searchParams.get('mode');
        const oobCode = searchParams.get('oobCode');
        const apiKey = searchParams.get('apiKey');
        const continueUrl = searchParams.get('continueUrl');
        const lang = searchParams.get('lang');

        console.log('Auth Action Router: Extracted parameters:', {
          mode,
          oobCode: oobCode ? `${oobCode.substring(0, 10)}...` : null,
          apiKey: apiKey ? `${apiKey.substring(0, 10)}...` : null,
          continueUrl,
          lang
        });

        // Validate required parameters
        if (!mode || !oobCode) {
          console.error('Auth Action Router: Missing required parameters:', { mode, hasOobCode: !!oobCode });
          setError('Invalid or missing action parameters.');
          setIsProcessing(false);
          return;
        }

        // Build query string to preserve all parameters
        const queryString = searchParams.toString();
        console.log('Auth Action Router: Query string to preserve:', queryString);

        // Set timeout to prevent infinite waiting
        timeoutId = setTimeout(() => {
          console.error('Auth Action Router: Navigation timeout - redirecting manually');
          window.location.href = `/update-password?${queryString}`;
        }, 3000);

        // Route based on action mode
        switch (mode) {
          case 'resetPassword':
            console.log('Auth Action Router: Redirecting to password reset with query:', queryString);
            navigate(`/update-password?${queryString}`, { replace: true });
            break;

          case 'verifyEmail':
            try { sessionStorage.setItem('email_verification_in_progress', 'true'); } catch (_) {}
            console.log('Auth Action Router: Redirecting to email verification with query:', queryString);
            navigate(`/verify-email?${queryString}`, { replace: true });
            break;

          case 'verifyAndChangeEmail':
            try { sessionStorage.setItem('email_verification_in_progress', 'true'); } catch (_) {}
            console.log('Auth Action Router: Redirecting to email change verification with query:', queryString);
            navigate(`/verify-email?${queryString}`, { replace: true });
            break;

          case 'recoverEmail':
            try { sessionStorage.setItem('email_verification_in_progress', 'true'); } catch (_) {}
            console.log('Auth Action Router: Redirecting to email recovery with query:', queryString);
            navigate(`/verify-email?${queryString}`, { replace: true });
            break;

          case 'signIn':
            console.log('Auth Action Router: Redirecting to sign in with email link with query:', queryString);
            navigate(`/signin?${queryString}`, { replace: true });
            break;

          default:
            console.error('Auth Action Router: Unknown action mode:', mode);
            setError(`Unknown action type: ${mode}`);
            setIsProcessing(false);
            return;
        }
        
        // If we reach here, navigation should have started
        console.log('Auth Action Router: Navigation initiated successfully');
        
      } catch (err) {
        console.error('Auth Action Router: Error processing action:', err);
        setError('Failed to process authentication action.');
        setIsProcessing(false);
      }
    };

    // Add a small delay to ensure DOM is ready
    const delayedProcessAction = setTimeout(() => {
      processAction();
    }, 100);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      clearTimeout(delayedProcessAction);
    };
  }, [searchParams, navigate]);

  const toggleTheme = () => {
    // This will be handled by the useDarkMode hook
  };

  const handleNavigation = () => {
    // Handle any navigation logic if needed
  };

  if (error) {
    return <AuthLoadingScreen />;
  }

  return <AuthLoadingScreen />;
} 